import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { RESET } from 'jotai/utils';
import * as React from 'react';

import type { AdaptiveContentBuilderStateContextProps, SiteVisitorSegment } from './types';

/**
 * Atom storing the current visitor claims being previewed in the app for a site.
 */
export const siteVisitorSegmentsAtom = atom<Record<string, SiteVisitorSegment>>({});

/**
 * Selector to get the visitor claims for a site.
 */
export const siteVisitorSegmentSelector = atomFamily((siteId: string) =>
    atom(
        (get) => {
            const siteClaims = get(siteVisitorSegmentsAtom);
            return siteClaims[siteId] ?? null;
        },
        (get, set, update: SiteVisitorSegment | null | typeof RESET) => {
            const nextSiteClaims = { ...get(siteVisitorSegmentsAtom) };
            if (update === RESET || update === null) {
                delete nextSiteClaims[siteId];
            } else {
                nextSiteClaims[siteId] = update;
            }
            set(siteVisitorSegmentsAtom, nextSiteClaims);
        }
    )
);

/**
 * Atom storing the visitor country being previewed in the app for each site.
 */
export const siteVisitorCountriesAtom = atom<Record<string, string>>({});

/**
 * Selector to get the visitor country being previewed for a site.
 */
export const siteVisitorCountrySelector = atomFamily((siteId: string) =>
    atom(
        (get) => get(siteVisitorCountriesAtom)[siteId] ?? null,
        (get, set, update: string | null) => {
            const nextSiteCountries = { ...get(siteVisitorCountriesAtom) };
            if (update === null) {
                delete nextSiteCountries[siteId];
            } else {
                nextSiteCountries[siteId] = update;
            }
            set(siteVisitorCountriesAtom, nextSiteCountries);
        }
    )
);

/**
 * Atom storing whether the visitor being previewed in the app is signed in, for each site.
 */
export const siteVisitorAuthenticatedAtom = atom<Record<string, boolean>>({});

/**
 * Selector to get whether the visitor being previewed for a site is signed in.
 */
export const siteVisitorAuthenticatedSelector = atomFamily((siteId: string) =>
    atom(
        (get) => get(siteVisitorAuthenticatedAtom)[siteId] ?? false,
        (get, set, update: boolean) => {
            set(siteVisitorAuthenticatedAtom, {
                ...get(siteVisitorAuthenticatedAtom),
                [siteId]: update,
            });
        }
    )
);

export const AdaptiveContentBuilderStateContext = React.createContext<
    AdaptiveContentBuilderStateContextProps | undefined
>(undefined);

export function useAdaptiveContentBuilderStateContext() {
    const context = React.useContext(AdaptiveContentBuilderStateContext);
    if (!context) {
        throw new Error(
            'useAdaptiveContentBuilderContext must be used within AdaptiveContentBuilderDialog'
        );
    }
    return context;
}
